import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TbSun, TbMoon, TbDeviceDesktop, TbAlertTriangle, TbEye, TbEyeOff } from "react-icons/tb";
import { useTheme } from "../../ThemeContext";
import "../styles/tokens.css";
import "./auth.css";

// The chrome every pre-login page shares: the public nav, and — where there is a form —
// the brand panel beside it.
//
// Wrapped in `.v2` so these pages resolve the same tokens as the application and follow
// the same light/dark/system preference. They are not inside the app shell, so without
// this they would need a second palette that could drift from the first.

const THEMES = [
  { value: "light", label: "Light", icon: TbSun },
  { value: "dark", label: "Dark", icon: TbMoon },
  { value: "system", label: "Follow system", icon: TbDeviceDesktop },
];

// Three, where the design kit drew two. "Follow the system" is a real choice and a
// two-state pill cannot say it; the shape is otherwise the kit's.
export const ThemeToggle = () => {
  const { mode, setMode } = useTheme();
  return (
    <div className="pnav-toggle" role="group" aria-label="Appearance">
      {THEMES.map(({ value, label, icon: Icon }) => (
        <button key={value} type="button" title={label} aria-label={label}
                aria-pressed={mode === value}
                className={mode === value ? "on" : ""}
                onClick={() => setMode(value)}>
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
};

export const PublicNav = () => (
  <nav className="pnav">
    <Link to="/login" className="pnav-brand">
      <img className="pnav-mark" src="/images/pipsqueak-mark.png" alt="" />
      <span>Pipsqueak</span>
    </Link>
    <div className="pnav-links">
      <Link className="pnav-link" to="/downloads">Downloads</Link>
      <Link className="pnav-link" to="/report">Report a bug</Link>
      <Link className="pnav-link" to="/terms">Terms</Link>
    </div>
    <ThemeToggle />
  </nav>
);

// The claim beside the form. Deliberately about what the product does for the person
// signing in, not about the product's own qualities.
export const BrandPanel = ({ modules = 14 }) => (
  <aside className="auth-brand">
    {/* The full lockup, not the bare mark: this panel is the one place with room for it,
        and it is what a first-time visitor should see the product called. Decorative —
        the product name is already read out by the nav, so an alt here would repeat it. */}
    <img className="auth-shield" src="/images/pipsqueak-shield.png" alt="" />
    <p className="auth-quote">
      Find what's exposed on the network, then hand the client a report that explains it.
    </p>
    <div className="auth-stats">
      <div className="auth-stat"><b>{modules}</b><span>Scan modules</span></div>
      <div className="auth-stat"><b>PDF</b><span>Client-ready export</span></div>
      <div className="auth-stat"><b>MFA</b><span>On every account</span></div>
    </div>
  </aside>
);

// A labelled field. `error` renders under the control and marks the input, because an
// error floating at the top of a form does not say which box caused it.
export const AuthField = ({ label, required, hint, error, children }) => (
  <div>
    <label className="auth-label">
      {label} {required && <span className="auth-req">*</span>}
    </label>
    {children}
    {error
      ? <p className="auth-error"><TbAlertTriangle size={13} style={{ flex: "none", marginTop: 1 }} />{error}</p>
      : hint && <p className="auth-help">{hint}</p>}
  </div>
);

// A password box with a show/hide button inside its right edge. Every other prop goes to
// the <input>, so id/htmlFor labelling, autoComplete and the handlers work as before.
// The button is type="button" so it never submits the form, and keeps focus in the
// field on mouse/touch so a phone keyboard does not close and reopen on every tap.
export const PasswordInput = ({ className = "auth-input", ...props }) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="auth-password">
      <input {...props} className={`${className} auth-password-input`} type={shown ? "text" : "password"} />
      <button type="button" className="auth-password-toggle"
              aria-label={shown ? "Hide password" : "Show password"} aria-pressed={shown}
              title={shown ? "Hide password" : "Show password"}
              disabled={props.disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShown((s) => !s)}>
        {shown ? <TbEyeOff size={18} /> : <TbEye size={18} />}
      </button>
    </div>
  );
};

const AuthShell = ({ children, brand = true, modules, footer }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`v2 pq-auth${isDarkMode ? " dark" : ""}`}>
      <div className="pq-auth-card">
        <PublicNav />
        {brand ? (
          <div className="auth">
            <div className="auth-form"><div className="auth-form-inner">{children}</div></div>
            <BrandPanel modules={modules} />
          </div>
        ) : children}
        {footer}
      </div>
    </div>
  );
};

export default AuthShell;
